
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Student } from "./types";
import i18n from "@/i18n";

export const useStudentSearch = (open: boolean, setOpen: (isOpen: boolean) => void) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch students when search query changes (debounced)
  useEffect(() => {
    // Only search if query has at least 3 characters
    if (!searchQuery || searchQuery.length < 3) {
      setStudents([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        // Call the search-students edge function instead of direct DB query
        const { data: responseData, error } = await supabase.functions.invoke('search-students', {
          body: { query: searchQuery }
        });

        if (error) {
          console.error("Error fetching students:", error);
          toast({
            title: i18n.t('form.fields.studentSearchErrorTitle'),
            description: i18n.t('form.fields.studentSearchErrorDesc'),
            variant: "destructive",
          });
          setStudents([]);
          return;
        }

        // The edge function returns data in the expected format
        const formattedStudents: Student[] = responseData?.students || [];
        
        setStudents(formattedStudents);
      } catch (error) {
        console.error("Error in API call:", error);
        toast({
          title: i18n.t('form.fields.studentSearchErrorTitle'),
          description: i18n.t('form.fields.studentNetworkErrorDesc'),
          variant: "destructive",
        });
        // Always maintain array structure even on error
        setStudents([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    students,
    isLoading
  };
};
